"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { Heading2 } from "../typography";
import { useRef } from "react";
import { PinnedScrollSection } from "../shared";

export const TheResults = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const achievements = [
    {
      title: "Government policy",
      description:
        "VMAP gives Commonwealth and state agencies an independent, continentally consistent reference for forest extent and loss — useable in greenhouse accounting, biodiversity reporting and land-use policy without waiting on bespoke studies.",
    },
    {
      title: "NGO advocacy and landscape programs",
      description:
        "WWF and partner organisations use VMAP to quantify forest loss in priority landscapes, target restoration programs and hold commitments accountable working with the same evidence base as government and industry.",
    },
    {
      title: "Commercial due diligence",
      description:
        "Exporters, traders and their auditors draw on VMAP's forest-loss record as one input to due diligence under the EU Deforestation Regulation (EUDR). Forest loss as mapped by VMAP does not by itself constitute deforestation under regulatory definitions — site-level assessment is still required — but a transparent, annually refreshed, continent-wide baseline makes that assessment far more tractable.",
    },
    {
      title: "Reproducible science",
      description:
        "The same analysis-ready Sentinel-2 stack, LiDAR labels and Terrak.io pipelines that produce VMAP are available to researchers — so follow-on science builds on the same foundations rather than starting from raw scenes.",
    },
  ];

  useSectionNavTheme({
    trigger: "#vmap-results",
    isDark: true,
    start: "center center",
    end: () => `+=${achievements.length * 500 + window.innerHeight}`,
  });

  return (
    <div
      ref={sectionRef}
      id="vmap-results"
      className="pt-20 lg:pb-64 lg:pt-44 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-haizea-green/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-haizea-blue/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-haizea-orange/25 rounded-full blur-2xl" />
      </div>

      <div className="results-content">
        <div className="max-w-[900px] mx-auto">
          <PinnedScrollSection
            title={
              <Heading2 className="text-slate-600 dark:text-slate-100">
                <span className="text-haizea-green">VMAP </span>
                in action
              </Heading2>
            }
            items={achievements}
            contentClassName="lg:h-[40vh]"
            yOffset={110}
            rangeBackground="linear-gradient(90deg, var(--color-haizea-green) 0%, var(--color-haizea-blue) 50%, var(--color-haizea-orange) 100%)"
            titleClassName="font-medium"
            descriptionClassName="text-slate-600 dark:text-slate-300"
            itemClassName="text-slate-700 dark:text-slate-200 my-4"
          />
        </div>
      </div>
    </div>
  );
};
